import React from 'react';

/**
 * Badge Styles
 *
 * Reusable status indicators, chips, and tags for displaying states, categories, or metadata.
 *
 * Usage Philosophy:
 * - 90% of cases: Use pre-composed badgeStyles (badgeStyles.success, badgeStyles.warning, etc.)
 * - 10% of cases: Use building blocks for custom combinations (badgeBase + badgeSizes + badgeVariants)
 *
 * @example Pre-composed (recommended)
 * ```tsx
 * <span style={badgeStyles.success}>Active</span>
 * <span style={badgeStyles.warning}>Pending</span>
 * <span style={badgeStyles.error}>Inactive</span>
 * ```
 *
 * @example Building blocks (edge cases)
 * ```tsx
 * <span style={{
 *   ...badgeBase,
 *   ...badgeSizes.large,
 *   ...badgeVariants.info
 * }}>
 *   Custom Badge
 * </span>
 * ```
 *
 * Color Reference:
 * - Success: Green (#10b981) - Active, enabled, completed states
 * - Warning: Orange/Amber (#f59e0b) - Pending, in-progress, caution states
 * - Error: Red (#ef4444) - Inactive, disabled, error states
 * - Info: Blue (#3b82f6) - Informational, neutral informational states
 * - Neutral: Gray (#6b7280) - Default, unclassified states
 */

// ============================================================================
// PRE-COMPOSED BADGE STYLES (90% usage)
// ============================================================================

export const badgeStyles = {
  /**
   * Success Badge - Green background
   * Use for: Active, enabled, completed, success states
   */
  success: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--success-color)',
    color: 'var(--on-primary-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Warning Badge - Orange/Amber background
   * Use for: Pending, in-progress, caution states
   */
  warning: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--warning-color)',
    color: 'var(--on-primary-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Error Badge - Red background
   * Use for: Inactive, disabled, error, failed states
   */
  error: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--error-color)',
    color: 'var(--on-primary-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Info Badge - Blue background
   * Use for: Informational, neutral informational states
   */
  info: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: '#3b82f6',
    color: 'var(--on-primary-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Neutral Badge - Gray background
   * Use for: Default, unclassified, miscellaneous states
   */
  neutral: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: '#6b7280',
    color: 'var(--on-primary-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Purple Badge - Purple background
   * Use for: Waiting, feedback, special states
   */
  purple: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: '#8b5cf6',
    color: 'var(--on-primary-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Success Outline Badge - Green border with green text
   * Use for: Lighter emphasis on success states
   */
  successOutline: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: 'var(--success-color)',
    border: '1px solid var(--success-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Warning Outline Badge - Orange border with orange text
   * Use for: Lighter emphasis on warning states
   */
  warningOutline: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: 'var(--warning-color)',
    border: '1px solid var(--warning-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Error Outline Badge - Red border with red text
   * Use for: Lighter emphasis on error states
   */
  errorOutline: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: 'var(--error-color)',
    border: '1px solid var(--error-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Info Outline Badge - Blue border with blue text
   * Use for: Lighter emphasis on informational states
   */
  infoOutline: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: 'var(--accent-color)',
    border: '1px solid var(--accent-color)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Neutral Outline Badge - Gray border with gray text
   * Use for: Lighter emphasis on neutral states
   */
  neutralOutline: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--text-secondary)',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /**
   * Purple Outline Badge - Purple border with purple text
   * Use for: Safety category, special emphasis on purple states
   */
  purpleOutline: {
    padding: '0.25rem 0.75rem',
    borderRadius: 'var(--radius)',
    backgroundColor: 'transparent',
    color: '#8b5cf6',
    border: '1px solid #8b5cf6',
    fontSize: '0.75rem',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
};

// ============================================================================
// COUNT BADGES (unread counts in navigation and tabs)
// ============================================================================

/**
 * Count Badge - Small pill for unread counts in navigation and tabs
 * Use for: Unread message counts, notification counts
 */
export const countBadge: React.CSSProperties = {
  backgroundColor: 'var(--primary-color)',
  color: 'var(--on-primary-color)',
  fontSize: '0.625rem',
  padding: '0 0.375rem',
  borderRadius: '9999px',
  fontWeight: '600',
  minWidth: '1rem',
  height: '0.875rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: '1',
};

/**
 * Count Badge Tiny - Extra small variant for collapsed nav icon overlays
 */
export const countBadgeTiny: React.CSSProperties = {
  ...countBadge,
  fontSize: '0.5rem',
  padding: '0 0.25rem',
  height: '0.6875rem',
  minWidth: '0.75rem',
};

/**
 * Count Badge Tab - Pill for unread counts inside tab buttons.
 * Different from countBadge (no fixed height, different padding/minWidth).
 */
export const countBadgeTab: React.CSSProperties = {
  backgroundColor: 'var(--primary-color)',
  color: 'var(--on-primary-color)',
  fontSize: '0.625rem',
  padding: '0.125rem 0.4375rem',
  borderRadius: '9999px',
  fontWeight: '600',
  minWidth: '1.125rem',
  textAlign: 'center',
};

// ============================================================================
// BUILDING BLOCKS (10% usage for edge cases)
// ============================================================================

/**
 * Badge Base - Common properties for all badges
 * Use with badgeSizes and badgeVariants for custom combinations
 */
export const badgeBase = {
  borderRadius: 'var(--radius)',
  fontWeight: '500',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  whiteSpace: 'nowrap',
} as React.CSSProperties;

/**
 * Badge Sizes - Predefined size variants
 */
export const badgeSizes = {
  small: {
    padding: '0.125rem 0.5rem',
    fontSize: '0.625rem',
  } as React.CSSProperties,

  medium: {
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
  } as React.CSSProperties,

  large: {
    padding: '0.375rem 1rem',
    fontSize: '0.875rem',
  } as React.CSSProperties,
};

/**
 * Badge Color Variants - Predefined color combinations
 */
export const badgeVariants = {
  success: {
    backgroundColor: 'var(--success-color)',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  warning: {
    backgroundColor: 'var(--warning-color)',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  error: {
    backgroundColor: 'var(--error-color)',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  info: {
    backgroundColor: '#3b82f6',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  neutral: {
    backgroundColor: '#6b7280',
    color: 'var(--on-primary-color)',
  } as React.CSSProperties,

  successOutline: {
    backgroundColor: 'transparent',
    color: 'var(--success-color)',
    border: '1px solid var(--success-color)',
  } as React.CSSProperties,

  warningOutline: {
    backgroundColor: 'transparent',
    color: 'var(--warning-color)',
    border: '1px solid var(--warning-color)',
  } as React.CSSProperties,

  errorOutline: {
    backgroundColor: 'transparent',
    color: 'var(--error-color)',
    border: '1px solid var(--error-color)',
  } as React.CSSProperties,

  infoOutline: {
    backgroundColor: 'transparent',
    color: 'var(--accent-color)',
    border: '1px solid var(--accent-color)',
  } as React.CSSProperties,

  neutralOutline: {
    backgroundColor: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--text-secondary)',
  } as React.CSSProperties,

  purpleOutline: {
    backgroundColor: 'transparent',
    color: '#8b5cf6',
    border: '1px solid #8b5cf6',
  } as React.CSSProperties,
};
